(() => {
  const parseJson = (element, fallback = {}) => {
    try { return JSON.parse(element?.textContent || '') || fallback; } catch (_) { return fallback; }
  };

  const formatMoney = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)} USD`;
  const themeRoutes = () => (typeof routes !== 'undefined' ? routes : (window.routes || {}));
  const storefrontApiAssetUrl = document.currentScript?.src
    ? new URL('shopifyStorefront.js', document.currentScript.src).href
    : '';
  let storefrontApiPromise = null;

  const ensureStorefrontApi = () => {
    if (window.ShopifyStorefrontAPI?.previewCartDiscount) return Promise.resolve(window.ShopifyStorefrontAPI);
    if (storefrontApiPromise) return storefrontApiPromise;
    storefrontApiPromise = new Promise((resolve, reject) => {
      if (!storefrontApiAssetUrl) { reject(new Error('Discount validation is unavailable.')); return; }
      const existingScript = [...document.scripts].find((script) => /shopifyStorefront\.js(?:\?|$)/.test(script.src));
      const script = existingScript || document.createElement('script');
      const timeout = setTimeout(() => reject(new Error('Discount validation timed out.')), 15000);
      const finish = () => {
        clearTimeout(timeout);
        if (window.ShopifyStorefrontAPI?.previewCartDiscount) resolve(window.ShopifyStorefrontAPI);
        else reject(new Error('Discount validation is unavailable.'));
      };
      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', () => { clearTimeout(timeout); reject(new Error('Discount validation is unavailable.')); }, { once: true });
      if (!existingScript) {
        script.src = storefrontApiAssetUrl;
        script.defer = true;
        document.head.appendChild(script);
      }
    }).catch((error) => {
      storefrontApiPromise = null;
      throw error;
    });
    return storefrontApiPromise;
  };

  const initPhotoBooth = (root) => {
    if (!root || root.dataset.pbReady === 'true') return;
    root.dataset.pbReady = 'true';

    const products = parseJson(root.querySelector('[data-pb-products]'));
    const imageMap = parseJson(root.querySelector('[data-pb-image-map]'));
    const expectedCode = (root.dataset.discountCode || 'GA10').trim().toUpperCase();
    root.querySelectorAll('[data-pb-asset]').forEach((image) => {
      const cdnUrl = imageMap[image.dataset.pbAsset];
      if (typeof cdnUrl === 'string' && /^https:\/\//i.test(cdnUrl)) image.src = cdnUrl;
    });

    root.querySelectorAll('[data-pb-copy-code]').forEach((button) => {
      button.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(expectedCode);
          const label = button.querySelector('em');
          if (label) { label.textContent = 'Copied'; setTimeout(() => { label.textContent = 'Copy'; }, 1600); }
        } catch (_) {}
      });
    });

    root.querySelectorAll('[data-pb-compare]').forEach((compare) => {
      const update = (clientX) => {
        const box = compare.getBoundingClientRect();
        const value = Math.max(2, Math.min(98, ((clientX - box.left) / box.width) * 100));
        compare.style.setProperty('--pb-split', `${value}%`);
      };
      compare.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        compare.setPointerCapture(event.pointerId);
        update(event.clientX);
      });
      compare.addEventListener('pointermove', (event) => {
        if (compare.hasPointerCapture(event.pointerId)) update(event.clientX);
      });
    });

    const effectCarousel = root.querySelector('[data-pb-effect-carousel]');
    if (effectCarousel) {
      const track = effectCarousel.querySelector('[data-pb-effect-track]');
      const slides = [...effectCarousel.querySelectorAll('[data-pb-effect-slide]')];
      const dots = [...effectCarousel.querySelectorAll('[data-pb-effect-dot]')];
      const previousButton = effectCarousel.querySelector('[data-pb-effect-prev]');
      const nextButton = effectCarousel.querySelector('[data-pb-effect-next]');
      const mobileQuery = window.matchMedia('(max-width: 749px)');
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      let activeIndex = 0;
      let scrollFrame = null;

      const pageLeft = (index) => Math.max(0, slides[index]?.offsetLeft - (slides[0]?.offsetLeft || 0));
      const setControls = (index) => {
        activeIndex = Math.max(0, Math.min(slides.length - 1, index));
        if (previousButton) previousButton.disabled = activeIndex === 0;
        if (nextButton) nextButton.disabled = activeIndex === slides.length - 1;
        dots.forEach((dot, dotIndex) => {
          if (dotIndex === activeIndex) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const syncFromScroll = () => {
        if (!mobileQuery.matches || !slides.length) { setControls(0); return; }
        const closestIndex = slides.reduce((closest, _slide, index) => (
          Math.abs(pageLeft(index) - track.scrollLeft) < Math.abs(pageLeft(closest) - track.scrollLeft) ? index : closest
        ), 0);
        setControls(closestIndex);
      };

      const goToPage = (index, behavior = reducedMotionQuery.matches ? 'auto' : 'smooth') => {
        const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
        setControls(targetIndex);
        track.scrollTo({ left: pageLeft(targetIndex), behavior });
      };

      track?.addEventListener('scroll', () => {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(syncFromScroll);
      }, { passive: true });
      track?.addEventListener('keydown', (event) => {
        if (!mobileQuery.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        goToPage(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });
      previousButton?.addEventListener('click', () => goToPage(activeIndex - 1));
      nextButton?.addEventListener('click', () => goToPage(activeIndex + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => goToPage(index)));

      if (typeof ResizeObserver !== 'undefined' && track) {
        const resizeObserver = new ResizeObserver(() => {
          if (mobileQuery.matches) goToPage(activeIndex, 'auto');
          else { track.scrollLeft = 0; setControls(0); }
        });
        resizeObserver.observe(track);
      }
      setControls(0);
    }

    const tabs = [...root.querySelectorAll('[data-pb-tab]')];
    const panels = [...root.querySelectorAll('[data-pb-panel]')];
    tabs.forEach((tab) => tab.addEventListener('click', () => {
      tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
      });
      panels.forEach((panel) => { panel.hidden = panel.dataset.pbPanel !== tab.dataset.pbTab; });
      const tabList = tab.closest('.pb-tabs');
      if (tabList && window.matchMedia('(max-width: 749px)').matches) {
        const listBox = tabList.getBoundingClientRect();
        const tabBox = tab.getBoundingClientRect();
        const targetLeft = tabList.scrollLeft + tabBox.left - listBox.left - (tabList.clientWidth - tabBox.width) / 2;
        tabList.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      }
    }));

    const modal = root.querySelector('[data-pb-modal]');
    if (!modal) return;
    const title = modal.querySelector('[data-pb-modal-title]');
    const image = modal.querySelector('[data-pb-modal-image]');
    const price = modal.querySelector('[data-pb-modal-price]');
    const options = modal.querySelector('[data-pb-options]');
    const discountInput = modal.querySelector('[data-pb-discount]');
    const discountStatus = modal.querySelector('[data-pb-discount-status]');
    const quantityLabel = modal.querySelector('[data-pb-qty]');
    const error = modal.querySelector('[data-pb-error]');
    let activeProduct = null;
    let selectedOptions = [];
    let quantity = 1;
    let discountApplied = false;
    let discountPreview = null;
    let discountValidating = false;

    const availableVariant = () => {
      if (!activeProduct?.variants?.length) return null;
      return activeProduct.variants.find((variant) => variant.options.every((value, index) => value === selectedOptions[index])) || null;
    };

    const renderPrice = () => {
      const variant = availableVariant();
      if (!variant) { price.textContent = ''; return; }
      const originalTotal = Number(variant.price || 0) * quantity;
      if (discountApplied && discountPreview?.variantId === String(variant.id) && discountPreview.quantity === quantity) {
        price.innerHTML = `<s>${formatMoney(discountPreview.originalCents)}</s><strong>${formatMoney(discountPreview.finalCents)}</strong>`;
      } else {
        price.textContent = formatMoney(originalTotal);
      }
    };

    const resetDiscount = () => {
      discountApplied = false;
      discountPreview = null;
      discountStatus.textContent = '';
      renderPrice();
    };

    const getCartSections = (cartDrawer) => cartDrawer?.getSectionsToRender?.() || [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];

    const hasRenderableCartSections = (state, sections) => {
      if (!state?.sections || !sections?.length) return false;
      return sections.every((section) => {
        const html = state.sections[section.id];
        if (typeof html !== 'string' || !html.trim()) return false;
        const selector = section.selector || '.shopify-section';
        return Boolean(new DOMParser().parseFromString(html, 'text/html').querySelector(selector));
      });
    };

    const cartHasDiscountCode = (cart, code) => {
      const applications = [
        ...(cart?.cart_level_discount_applications || []),
        ...(cart?.discount_applications || [])
      ];
      return applications.some((application) => String(application?.title || '').trim().toUpperCase() === code);
    };

    const publishCartState = (state, variantId) => {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'photo-booth-landing',
          productVariantId: String(variantId),
          cartData: state
        });
      }
    };

    const applyDiscountToCart = async (code, sections) => {
      const cartUpdateUrl = themeRoutes().cart_update_url;
      if (!cartUpdateUrl) return { applied: false, state: null };
      const response = await fetch(cartUpdateUrl, {
        ...(typeof fetchConfig === 'function' ? fetchConfig() : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
        }),
        body: JSON.stringify({
          discount: code,
          sections: sections.map((section) => section.id),
          sections_url: window.location.pathname
        })
      });
      if (!response.ok) return { applied: false, state: null };
      const state = await response.json();
      if (state?.errors) return { applied: false, state };
      return { applied: cartHasDiscountCode(state, code), state };
    };

    const renderOptions = () => {
      if (!activeProduct) return;
      options.innerHTML = (activeProduct.options || []).map((optionName, optionIndex) => {
        const values = [...new Set(activeProduct.variants.map((variant) => variant.options[optionIndex]))];
        return `<div class="pb-option"><p>${optionName}</p><div class="pb-option__values">${values.map((value) => `<button type="button" class="${value === selectedOptions[optionIndex] ? 'is-selected' : ''}" data-pb-option="${optionIndex}" data-pb-value="${String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;')}">${value}</button>`).join('')}</div></div>`;
      }).join('');
      renderPrice();
    };

    const openModal = (handle) => {
      const product = products[handle];
      if (!product?.variants?.length) {
        window.location.href = `/products/${encodeURIComponent(handle)}`;
        return;
      }
      activeProduct = product;
      const firstAvailable = product.variants.find((variant) => variant.available) || product.variants[0];
      selectedOptions = [...firstAvailable.options];
      quantity = 1;
      discountApplied = false;
      discountPreview = null;
      discountValidating = false;
      quantityLabel.textContent = '1';
      discountInput.value = expectedCode;
      discountStatus.textContent = '';
      error.textContent = '';
      title.textContent = product.title;
      image.src = product.featured_image || firstAvailable.featured_image?.src || '';
      image.alt = product.title;
      renderOptions();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      modal.querySelector('[data-pb-close]')?.focus();
    };

    const closeModal = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
    };

    root.querySelectorAll('[data-pb-quick-add]').forEach((button) => button.addEventListener('click', () => openModal(button.dataset.pbQuickAdd)));
    modal.querySelector('[data-pb-close]')?.addEventListener('click', closeModal);
    modal.addEventListener('click', (event) => { if (event.target === modal) closeModal(); });
    modal.querySelector('[data-pb-options]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-pb-option]');
      if (!button) return;
      selectedOptions[Number(button.dataset.pbOption)] = button.dataset.pbValue;
      resetDiscount();
      renderOptions();
    });
    modal.querySelector('[data-pb-qty-minus]')?.addEventListener('click', () => {
      quantity = Math.max(1, quantity - 1);
      quantityLabel.textContent = String(quantity);
      resetDiscount();
    });
    modal.querySelector('[data-pb-qty-plus]')?.addEventListener('click', () => {
      quantity += 1;
      quantityLabel.textContent = String(quantity);
      resetDiscount();
    });
    modal.querySelector('[data-pb-apply]')?.addEventListener('click', async (event) => {
      if (discountValidating) return;
      const variant = availableVariant();
      const code = discountInput.value.trim().toUpperCase();
      if (!variant) { resetDiscount(); discountStatus.textContent = 'Choose a valid option combination.'; return; }
      if (!variant.available) { resetDiscount(); discountStatus.textContent = 'This option is currently unavailable.'; return; }
      if (code !== expectedCode) { resetDiscount(); discountStatus.textContent = 'Enter the valid offer code.'; return; }
      const applyButton = event.currentTarget;
      const originalLabel = applyButton.textContent;
      discountValidating = true;
      applyButton.disabled = true;
      applyButton.textContent = 'Checking…';
      discountStatus.textContent = '';
      try {
        const storefrontApi = await ensureStorefrontApi();
        const line = { merchandiseId: `gid://shopify/ProductVariant/${variant.id}`, quantity };
        const preview = await storefrontApi.previewCartDiscount([line], code);
        const discountCode = preview?.discountCodes?.find((item) => item.code?.toUpperCase() === code) || preview?.discountCodes?.[0];
        const originalCents = Number(variant.price || 0) * quantity;
        const finalCents = Math.round(Number(preview?.lines?.nodes?.[0]?.cost?.totalAmount?.amount || 0) * 100);
        if (!discountCode?.applicable || finalCents <= 0 || finalCents >= originalCents) {
          resetDiscount();
          discountStatus.textContent = 'This product is not eligible for this discount code.';
          return;
        }
        discountApplied = true;
        discountPreview = { variantId: String(variant.id), quantity, originalCents, finalCents };
        discountStatus.textContent = `Discount verified. CODE: ${code}`;
        renderPrice();
      } catch (_) {
        resetDiscount();
        discountStatus.textContent = 'Discount validation is unavailable. Please try again later.';
      } finally {
        discountValidating = false;
        applyButton.disabled = false;
        applyButton.textContent = originalLabel;
      }
    });

    modal.querySelector('[data-pb-add]')?.addEventListener('click', async (event) => {
      const variant = availableVariant();
      if (discountValidating) { error.textContent = 'Wait for discount validation to finish.'; return; }
      if (!variant) { error.textContent = 'Choose a valid option combination.'; return; }
      if (!variant.available) { error.textContent = 'This option is currently unavailable.'; return; }
      const button = event.currentTarget;
      button.disabled = true;
      button.textContent = 'Adding…';
      error.textContent = '';
      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = getCartSections(cartDrawer);
        const formData = new FormData();
        formData.append('id', String(variant.id));
        formData.append('quantity', String(quantity));
        formData.append('sections', sections.map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cartDrawer?.setActiveElement?.(button);

        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
          method: 'POST',
          headers: { Accept: 'application/javascript' }
        };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;
        const response = await fetch(themeRoutes().cart_add_url || '/cart/add.js', {
          ...config
        });
        const addState = await response.json();
        if (!response.ok || addState.status) throw new Error(addState.description || 'Unable to add this item.');

        let finalState = addState;
        let discountWarning = '';
        if (discountApplied) {
          try {
            const discountResult = await applyDiscountToCart(expectedCode, sections);
            if (discountResult.state) finalState = discountResult.state;
            if (!discountResult.applied) discountWarning = `Item added, but ${expectedCode} could not be applied.`;
          } catch (_) {
            discountWarning = `Item added, but ${expectedCode} could not be applied.`;
          }
        }

        closeModal();
        if (!cartDrawer?.renderContents || !hasRenderableCartSections(finalState, sections)) {
          window.location.href = themeRoutes().cart_url || '/cart';
          return;
        }

        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(finalState);
        publishCartState(finalState, variant.id);
        if (discountApplied) document.dispatchEvent(new CustomEvent('cart:updated'));
        if (discountWarning) {
          const cartError = document.querySelector('#CartDrawer-CartErrors');
          if (cartError) cartError.textContent = discountWarning;
        }
      } catch (requestError) {
        error.textContent = requestError.message || 'Unable to add this item. Please try again.';
      } finally {
        button.disabled = false;
        button.textContent = 'Add to cart';
      }
    });

    modal.querySelector('[data-pb-buy]')?.addEventListener('click', () => {
      const variant = availableVariant();
      if (!variant) return;
      const code = discountApplied ? `&discount=${encodeURIComponent(expectedCode)}` : '';
      window.location.href = `/cart/${variant.id}:${quantity}?checkout${code}`;
    });

    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && modal.classList.contains('is-open')) closeModal(); });
  };

  const boot = () => document.querySelectorAll('[data-pb-section]').forEach(initPhotoBooth);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (event) => initPhotoBooth(event.target.querySelector('[data-pb-section]')));
})();
